import ProfileCountryModel from "../Models/ProfileCountryModel";
import ProfileHttps from "../../../Https/ProfileHttps";
import ProfileFacade from "../Facade/ProfileFacade";
import FollowingModel from "../Models/Following/FollowingModel";
import InterestModel from "../Models/InterestModel";
import FullnessProfile from "../Models/FullnessProfile/FullnessProfile";
import FollowFilters from "../Models/FollowFilters";
import PaginationModel from "../../../Global/Models/Pagination/PaginationModel";
import ProfileStatsModel from "../Models/Stats/ProfileStatsModel";
import ActivityHttps from "../../../Https/ActivityHttps";
import ProfileService from "../Service/ProfileService";
import SDKIdsRemapper from "../../../Remapper/SDKIdsRemapper";
import FansUnitedSdkException from "../../../Exception/FansUnitedSdkException";
import StandardFansUnitedException from "../../../Exception/StandardFansUnitedException";
import { ErrorCodes } from "../../../Exception/ErrorCodes";
import { ErrorStatuses } from "../../../Exception/ErrorStatuses";
import { ErrorMessages } from "../../../Global/Messages/Messages";
import { LocalCacheInterface } from "../../../Global/Interfaces/GlobalInterfaces";
import SDKConfigurationModel from "../../../Configurator/Models/SDKConfiguraitonModel";
import DetailedProfileModel from "../Models/DetailedProfileModel";
import ConsentModel from "../Models/Consent/ConsentModel";

export default class OwnProfileBuilder {
    private idsRemapper: SDKIdsRemapper = null;
    private profileHttps: ProfileHttps = null;
    private profileService: ProfileService = null;
    private localCache: LocalCacheInterface = null;
    private profileFacade: ProfileFacade = null;
    private ownProfile: DetailedProfileModel = null;
    private modified: boolean = false;
    private expireAt: number = 0;
    private promises: Promise<any>[] = [];
    private isFetching: boolean = false;
    private configuration: SDKConfigurationModel = null;
    private newProfileCheckResult: boolean | null = null;
    private fetchProfilePromise: Promise<DetailedProfileModel> | null = null; // Cache the fetch promise

    constructor(configuration: SDKConfigurationModel, idsRemapper: SDKIdsRemapper, localCache: LocalCacheInterface, profileHttps: ProfileHttps, activityHttps: ActivityHttps) {
        this.configuration = configuration;
        this.idsRemapper = idsRemapper;
        this.localCache = localCache;
        this.profileHttps = profileHttps;
        this.profileService = new ProfileService();
        this.profileFacade = new ProfileFacade(localCache, profileHttps, activityHttps);
    }

    private getOwnProfile = async (): Promise<DetailedProfileModel> => {
        while (this.isFetching) {
            console.log("Another thread is fetching profile...");
            await this.delay(100);
        }

        if (this.modified && this.ownProfile) {
            console.log("Timestamp is less than 10 minutes OR profile is modifying ");
            return this.ownProfile;
        }

        const now = Math.floor(Date.now() / 1000);

        // Check for new profile only once and cache the result
        if (this.newProfileCheckResult === null) {
            this.newProfileCheckResult = await this.isNewProfileRequested();
        }

        // If we need to fetch (no profile, expired, or new profile) and we're not already fetching
        const needsToFetch = !this.ownProfile || this.expireAt < now || this.newProfileCheckResult;

        if (needsToFetch) {
            // If we're already fetching, wait for that promise
            if (this.fetchProfilePromise) {
                return await this.fetchProfilePromise;
            }

            // Start a new fetch
            this.fetchProfilePromise = this.doFetchProfile();

            try {
                const profile = await this.fetchProfilePromise;
                return profile;
            } finally {
                // Clear the promise when done (success or failure)
                this.fetchProfilePromise = null;
            }
        }

        return this.ownProfile;
    };

    private doFetchProfile = async (): Promise<DetailedProfileModel> => {
        this.isFetching = true;
        this.expireAt = Math.floor(Date.now() / 1000) + 600; // 10 minutes in seconds

        try {
            await this.clearAllProfileData();
            const profile = await this.profileFacade.getOwn();
            this.setOwnProfile(profile);
            this.modified = false;
            this.isFetching = false;

            return this.ownProfile;
        } catch (e) {
            this.isFetching = false;
            throw e;
        }
    };

    private delay = (milliseconds: number) => {
        return new Promise(resolve => {
            setTimeout(resolve, milliseconds);
        });
    };

    private setOwnProfile = (ownProfile: DetailedProfileModel) => {
        this.ownProfile = ownProfile;
    };

    /**
     * Clears the cached result of the new profile check. Used for direct method calls
     */
    private resetProfileCheckResult = () => {
        this.newProfileCheckResult = null;
    }

    public getInfo = async (): Promise<DetailedProfileModel> => {
        this.resetProfileCheckResult()
        const profile = await this.getOwnProfile();
        const profileCopy = JSON.parse(JSON.stringify(profile));
        delete profileCopy.interests;

        return profileCopy;
    };

    public showInterests = async (): Promise<InterestModel[]> => {
        this.resetProfileCheckResult()
        const ownProfile = await this.getOwnProfile();
        const ownProfileCopy = JSON.parse(JSON.stringify(ownProfile));

        return await this.idsRemapper.remapInterests(ownProfileCopy.interests);
    };

    public showFullInterests = async () => {
        this.resetProfileCheckResult()
        const ownProfile = await this.getOwnProfile();
        const ownProfileCopy = JSON.parse(JSON.stringify(ownProfile));

        return await this.idsRemapper.showFullInterests(ownProfileCopy.interests);
    };

    public getFollowers = async (filters?: FollowFilters): Promise<PaginationModel> => {
        return await this.profileFacade.getFollowers(filters);
    };

    public getFollowing = async (filters?: FollowFilters): Promise<PaginationModel> => {
        return await this.profileFacade.getFollowing(filters);
    };

    public follow = async (profiles: string[]): Promise<FollowingModel[]> => {
        const followingResponse = await this.profileFacade.follow(profiles);
        const newProfile = await this.profileFacade.updateOwnProfile();
        this.setOwnProfile(newProfile);

        return followingResponse;
    };

    public unfollow = async (profiles: string[]): Promise<boolean> => {
        const unfollowSuccess = await this.profileFacade.unfollow(profiles);
        const newProfile = await this.profileFacade.updateOwnProfile();
        this.setOwnProfile(newProfile);

        return unfollowSuccess;
    };

    public getStats = async (): Promise<ProfileStatsModel> => {
        await this.clearAllProfileData();

        const profileStats =  await this.profileFacade.getStats();

        return await this.idsRemapper.remapProfileStats(profileStats);
    };

    public getBadges = async (disableCache?: boolean): Promise<string[]> => {
        await this.clearAllProfileData();

        return await this.profileFacade.getBadges(disableCache);
    };

    private addInterestPromise = async (interest: InterestModel): Promise<OwnProfileBuilder> => {
        this.modified = true;
        const copyInterest = { ...interest };
        await this.getOwnProfile();
        let localInterest: InterestModel = null;

        if (interest.source !== "custom") {
            localInterest = await this.idsRemapper.remapInterestToNative(copyInterest);
        } else {
            localInterest = copyInterest;
        }

        const newInterests = this.ownProfile.interests.filter((profileInterest: InterestModel) => {
            return (profileInterest.id !== localInterest.id || profileInterest.type !== localInterest.type)
            || profileInterest.source !== localInterest.source;
        });

        this.ownProfile.interests = [...newInterests];
        this.ownProfile.interests.push(localInterest);

        return this;
    };

    public addInterest = (interest: InterestModel): OwnProfileBuilder => {
        this.promises.push(this.addInterestPromise(interest));

        return this;
    };

    private removeInterestPromise = async (interest: InterestModel): Promise<OwnProfileBuilder> => {
        this.modified = true;
        const copyInterest = {...interest};
        await this.getOwnProfile();
        let localInterest: InterestModel = null;

        if (interest.source !== "custom") {
            localInterest = await this.idsRemapper.remapInterestToNative(copyInterest);
        } else {
            localInterest = copyInterest;
        }

        this.ownProfile.interests = this.ownProfile.interests.filter((profileInterest: InterestModel) => {
            return (profileInterest.id !== localInterest.id || profileInterest.type !== localInterest.type)
                || profileInterest.source !== localInterest.source;
        });

        return this;
    };

    public removeInterest = (interest: InterestModel): OwnProfileBuilder => {
        this.promises.push(this.removeInterestPromise(interest));

        return this;
    };

    private setBirthdatePromise = async (birthDate?: string): Promise<OwnProfileBuilder> => {
        this.modified = true;
        await this.getOwnProfile();

        if (birthDate) {
            this.ownProfile.birthDate = birthDate;
        } else {
            this.ownProfile.birthDate = null;
        }

        return this;
    };

    public setBirthdate = (birthDate?: string): OwnProfileBuilder => {
        this.promises.push(this.setBirthdatePromise(birthDate));

        return this;
    };

    private setGenderPromise = async (gender: string): Promise<OwnProfileBuilder> => {
        this.modified = true;
        await this.getOwnProfile();

        if (gender === "male" || gender === "female") {
            this.ownProfile.gender = gender;
        } else {
            this.ownProfile.gender = "unspecified";
        }

        return this;
    };

    public setGender = (gender: string): OwnProfileBuilder => {
        this.promises.push(this.setGenderPromise(gender));

        return this;
    };

    private setNicknamePromise = async (nickname: string): Promise<OwnProfileBuilder> => {
        this.modified = true;
        await this.getOwnProfile();
        this.ownProfile.nickname = nickname;

        return this;
    };

    public setNickname = (nickname?: string): OwnProfileBuilder => {
        this.promises.push(this.setNicknamePromise(nickname));

        return this;
    };

    private setNamePromise = async (name: string): Promise<OwnProfileBuilder> => {
        this.modified = true;
        await this.getOwnProfile();
        this.ownProfile.name = name;

        return this;
    };

    public setName = (name: string): OwnProfileBuilder => {
        this.promises.push(this.setNamePromise(name));

        return this;
    };

    private setCountryPromise = async (countryId?: string): Promise<OwnProfileBuilder> => {
        this.modified = true;
        await this.getOwnProfile();

        if (countryId && !this.ownProfile.country) {
            this.ownProfile.country = new ProfileCountryModel();
            this.ownProfile.country.id = countryId;
        } else if (countryId && this.ownProfile.country) {
            this.ownProfile.country.id = countryId;
        } else {
            this.ownProfile.country = null;
        }

        return this;
    };

    public setCountry = (countryId?: string): OwnProfileBuilder => {
        this.promises.push(this.setCountryPromise(countryId));

        return this;
    };

    public showFullnessProfile = async (): Promise<FullnessProfile> => {
        this.resetProfileCheckResult();
        const profile = await this.getOwnProfile();
        const profileCopy = JSON.parse(JSON.stringify(profile));

        return this.profileFacade.showFullnessProfile(profileCopy);
    };

    private setAvatarPromise = async (avatar: string): Promise<OwnProfileBuilder> => {
        this.modified = true;
        await this.getOwnProfile();

        if (this.profileService.isAvatarValidURL(avatar)) {
            this.ownProfile.avatar = avatar;
        } else {
            if (this.configuration.errorHandlingMode === "default") {
                throw new FansUnitedSdkException(ErrorCodes.BAD_METHOD_CALL, ErrorStatuses.INVALID_AVATAR, ErrorMessages.INVALID_AVATAR);
            } else if (this.configuration.errorHandlingMode === "standard") {
                throw new StandardFansUnitedException(ErrorCodes.BAD_METHOD_CALL, ErrorStatuses.INVALID_AVATAR, ErrorMessages.INVALID_AVATAR);
            }
        }

        return this;
    };

    public setAvatar = (avatar: string): OwnProfileBuilder => {
        this.promises.push(this.setAvatarPromise(avatar));

        return this;
    };

    public delete = async (): Promise<boolean> => {
        await this.profileHttps.deleteOwnProfile();
        await this.profileFacade.clearLocalStorageData();

        return true;
    };

    public deleteData = async (): Promise<boolean> => {
        await this.profileHttps.deleteUserData();
        await this.profileFacade.clearLocalStorageData();

        return true;
    };

    private setEmailPromise = async (email: string): Promise<OwnProfileBuilder> => {
        this.modified = true;
        await this.getOwnProfile();

        if (typeof email === 'string' && email) {
            this.ownProfile.email = email;
        } else {
            console.warn(`Wrong email argument: ${email}. Will be ignored when updating profile`)
        }

        return this;
    };

    public setEmail = (email: string): OwnProfileBuilder => {
        this.promises.push(this.setEmailPromise(email));

        return this;
    };

    private setPhoneCountryCodePromise = async (phoneCountryCode: string | null): Promise<OwnProfileBuilder> => {
        this.modified = true;
        await this.getOwnProfile();

        if (phoneCountryCode === null ||
            (typeof phoneCountryCode === 'string' && phoneCountryCode)
        ) {
            this.ownProfile.phoneCountryCode = phoneCountryCode;
        } else {
            console.warn(`Wrong phoneCountryCode argument: ${phoneCountryCode}. Will be ignored when updating profile`)
        }

        return this;
    };

    public setPhoneCountryCode = (phoneCountryCode: string | null): OwnProfileBuilder => {
        this.promises.push(this.setPhoneCountryCodePromise(phoneCountryCode));

        return this;
    };

    private setPhoneNumberPromise = async (phoneNumber: string | null): Promise<OwnProfileBuilder> => {
        this.modified = true;
        await this.getOwnProfile();

        if (phoneNumber === null ||
            (typeof phoneNumber === 'string' && phoneNumber)
        ) {
            this.ownProfile.phoneNumber = phoneNumber;
        } else {
            console.warn(`Wrong phoneNumber argument: ${phoneNumber}. Will be ignored when updating profile`)
        }

        return this;
    };

    public setPhoneNumber = (phoneNumber: string | null): OwnProfileBuilder => {
        this.promises.push(this.setPhoneNumberPromise(phoneNumber));

        return this;
    };

    public getExternalActivities = async () => {
        return await this.profileHttps.getOwnExternalActivities();
    };

    public update = async (): Promise<DetailedProfileModel> => {
        if (this.promises.length === 0) {
            return this.ownProfile;
        }

        await Promise.all(this.promises);
        // Reset promises array for next operation cycle
        this.promises = [];
        this.resetProfileCheckResult()

        const oldProfile = await this.profileFacade.getOwn();
        const newProfile = await this.getOwnProfile();
        const profileToPatch = this.profileService.extractUpdatedProps(oldProfile, newProfile);

        this.modified = false;
        this.expireAt = 0;

        if (Object.keys(profileToPatch).length) {
            const patchedProfile = await this.profileHttps.updateProfile(profileToPatch);
            const copyPatchedProfile = JSON.parse(JSON.stringify(patchedProfile));
            this.setOwnProfile(copyPatchedProfile);
            await this.localCache.setOwnProfile(copyPatchedProfile);
            delete patchedProfile.interests;

            return patchedProfile;
        }

        const profile = JSON.parse(JSON.stringify(this.ownProfile));
        delete profile.interests;

        return profile;
    };

    public getConsents = async (): Promise<ConsentModel[]> => {
        return await this.profileHttps.getConsents();
    };

    public deleteConsent = async (consentId: string): Promise<boolean> => {
        return await this.profileHttps.deleteConsent(consentId);
    };

    private clearAllProfileData = async () => {
        // When a new profile is requested, we need to clear the old profile's data in Local Storage
        if (this.newProfileCheckResult === true) {
            await this.profileFacade.clearLocalStorageData();
        }
    };

    /**
     * The method checks if the profile from the token is different than the profile
     * from memory or Local Storage.
     */
    private isNewProfileRequested = async () => {
        const userIdFromToken = this.profileHttps.extractUserIdFromToken();

        if (!userIdFromToken) {
            // When the ID is missing from the JWT, we can't compare the profile
            return false;
        }

        if (!this.ownProfile) {
            // No profile stored in memory
            const profile = await this.profileFacade.getOwnProfileFromCache();

            if (profile && Object.keys(profile).length) {
                if (profile.id !== userIdFromToken) {
                    return true;
                }
            }

            return false;
        }

        if (this.ownProfile.id !== userIdFromToken) {
            return true;
        }

        return false;
    };
}